import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { Boxes, PackageCheck, Truck } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RECEIVE_CONFIRM_SCOPE, type ReceiveConfirmArrivalResultDto } from '@/entities/receive';

import { CONFIRM_ARRIVAL_FORM_ID, CONFIRM_ARRIVAL_TAB, type ConfirmArrivalTab } from '../model/constants';
import { useConfirmArrivalForm } from '../model/use-confirm-arrival-form';
import type { ConfirmArrivalTarget } from '../model/types';
import { ConfirmArrivalDocuments } from './confirm-arrival-documents';
import { ConfirmArrivalHeaderFields } from './confirm-arrival-header-fields';
import { ConfirmArrivalLinesTable } from './confirm-arrival-lines-table';
import { ConfirmArrivalResult } from './confirm-arrival-result';
import { ConfirmArrivalContextError } from './confirm-arrival-context-error';
import { ConfirmArrivalSkeleton } from './confirm-arrival-skeleton';
import { ConfirmArrivalWarnings } from './confirm-arrival-warnings';
import { TransportationDetailsPlaceholder } from './transportation-details-placeholder';

export interface ReceiveConfirmArrivalModalProps {
  /**
   * The consignment row being received — or, with `scope: 'WAYBILL'`, the anchor
   * of its waybill. The modal is mounted only while a row is selected, so every
   * open starts from a clean form and a fresh context call.
   */
  target: ConfirmArrivalTarget;
  onClose: () => void;
  onSuccess?: (result: ReceiveConfirmArrivalResultDto) => void;
  /** Takes over after *Receive and Release* — see `useConfirmArrivalForm`. */
  onReceiveAndRelease?: (result: ReceiveConfirmArrivalResultDto) => void;
}

export function ReceiveConfirmArrivalModal({
  target,
  onClose,
  onSuccess,
  onReceiveAndRelease
}: ReceiveConfirmArrivalModalProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ConfirmArrivalTab>(CONFIRM_ARRIVAL_TAB.ITEMS);

  const {
    form,
    context,
    attachments,
    unitMode,
    setUnitMode,
    result,
    isLoading,
    isRefreshingContext,
    isSubmitting,
    contextError,
    handleLocationChange,
    handleResetQuantities,
    canReceiveAndRelease,
    handleSubmit,
    handleReceiveAndRelease,
    handleClose
  } = useConfirmArrivalForm({ target, onClose, onSuccess, onReceiveAndRelease });

  // Shown only until the context call resolves — the backend assembles and
  // localizes the real title (`dialogTitle`). The legacy `receive.confirm.title`
  // key cannot be reused here: it carries a JSF `{0}` placeholder that i18next
  // does not interpolate.
  const isWaybill = target.scope === RECEIVE_CONFIRM_SCOPE.WAYBILL;
  const fallbackTitle = isWaybill
    ? t('receive.confirm.waybillFallbackTitle', 'Receive waybill {{waybill}}', { waybill: target.waybill })
    : target.consignmentNumber
      ? t('receive.confirm.fallbackTitle', 'Confirm receipt of goods for Consignment: {{consignment}}', {
          consignment: target.consignmentNumber
        })
      : t('receive.button.desc', 'Confirm received goods');

  const isDone = result !== null;

  /**
   * The dialog body, as a chain of early returns rather than nested ternaries.
   *
   * A plain function call — **not** a component. Declaring a component inside
   * another one gives it a new identity on every render, which would remount the
   * whole form and lose focus on each keystroke; calling a function that returns
   * JSX just inlines the elements.
   *
   * Order matters: the result is checked first because recording the receipt
   * invalidates the `Receive` tag, which refetches this very context for a
   * shipment that is no longer awaiting receipt. Were the error case first, a
   * failed refetch would replace the summary — and the GRN numbers it carries
   * exist nowhere else.
   */
  const renderBody = () => {
    if (result) return <ConfirmArrivalResult result={result} />;
    if (isLoading) return <ConfirmArrivalSkeleton />;
    if (contextError || !context) return <ConfirmArrivalContextError />;

    return (
      <form
        id={CONFIRM_ARRIVAL_FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void handleSubmit(event);
        }}
        className="space-y-6">
        <ConfirmArrivalWarnings warnings={context.warnings} />

        <Tabs value={tab} onValueChange={(value) => setTab(value as ConfirmArrivalTab)}>
          <TabsList variant="line">
            <TabsTrigger value={CONFIRM_ARRIVAL_TAB.ITEMS}>
              <PackageCheck className="mr-2 h-4 w-4" />
              {t('newcmr.items.label', 'Items')}
            </TabsTrigger>
            <TabsTrigger value={CONFIRM_ARRIVAL_TAB.TRANSPORT}>
              <Boxes className="mr-2 h-4 w-4" />
              {t('onlinesrf.transport.details.label', 'Details of Transportation')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={CONFIRM_ARRIVAL_TAB.ITEMS} className="space-y-6 pt-4">
            <ConfirmArrivalHeaderFields
              form={form}
              context={context}
              isRefreshingContext={isRefreshingContext}
              onLocationChange={handleLocationChange}
              isWaybill={isWaybill}
            />

            <ConfirmArrivalLinesTable
              control={form.control}
              setValue={form.setValue}
              context={context}
              unitMode={unitMode}
              onUnitModeChange={setUnitMode}
              onResetQuantities={handleResetQuantities}
            />

            <ConfirmArrivalDocuments
              attachments={context.attachments}
              allowedTypes={context.allowedAttachmentTypes}
              pending={attachments.pending}
              comment={attachments.comment}
              onAddFiles={attachments.addFiles}
              onTypeChange={attachments.setAttachmentType}
              onRemove={attachments.removeFile}
              onCommentChange={attachments.setComment}
              pendingTitle={
                isWaybill
                  ? t('receive.attachments.toUploadWaybill', 'Will be attached to the receipt of every consignment')
                  : undefined
              }
            />

            <div className="flex items-center gap-2">
              <Controller
                name="printGoodsReceivedNote"
                control={form.control}
                render={({ field }) => (
                  <Checkbox id="printGoodsReceivedNote" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="printGoodsReceivedNote" className="text-sm font-normal">
                {t('dispatch.print.goods.receive.note.button', 'Print Goods Received Note')}
              </Label>
            </div>
          </TabsContent>

          <TabsContent value={CONFIRM_ARRIVAL_TAB.TRANSPORT} className="pt-4">
            <TransportationDetailsPlaceholder />
          </TabsContent>
        </Tabs>
      </form>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        // `sm:max-w-…` is not decoration: DialogContent's own base classes carry
        // `sm:max-w-lg`, and tailwind-merge keys by modifier — an unprefixed
        // `max-w-*` does not replace it, so the dialog would stay 512px wide on
        // any viewport ≥640px. The unprefixed bound below it keeps the 2rem
        // gutter on narrow screens.
        className="grid max-h-[92vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sm:max-w-[min(1200px,calc(100%-2rem))]"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-muted-foreground" aria-hidden />
            {context?.dialogTitle || fallbackTitle}
          </DialogTitle>
          <DialogDescription>
            {t('receive.confirm.description', 'Record where, when and in what condition the cargo arrived.')}
          </DialogDescription>
        </DialogHeader>

        {/* The only vertical scroller in the dialog, so the title and the action
            buttons stay put however long the line list gets. `min-h-0` lets the
            grid row actually shrink; `overflow-x-hidden` is not redundant — per
            css-overflow-3 an axis left `visible` next to a non-`visible` one
            computes to `auto`, so `overflow-y-auto` alone would quietly make this
            a horizontal scroller and shift the whole form sideways. */}
        <div className="min-h-0 overflow-y-auto overflow-x-hidden">{renderBody()}</div>

        <DialogFooter className="gap-2 border-t pt-4 sm:justify-center">
          <ActionButton type="button" intent="dismiss" className="min-w-[140px]" onClick={handleClose}>
            {t('gen.closedialog.label', 'Close Dialog')}
          </ActionButton>
          {!isDone && (
            <ActionButton
              intent="primary"
              type="submit"
              form={CONFIRM_ARRIVAL_FORM_ID}
              className="min-w-[140px]"
              disabled={isSubmitting || isLoading || !context}>
              {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : <PackageCheck className="mr-2 h-4 w-4" />}
              {t('receive.button.label', 'Receive')}
            </ActionButton>
          )}
          {!isDone && canReceiveAndRelease && (
            <ActionButton
              type="button"
              intent="action"
              className="min-w-[140px]"
              disabled={isSubmitting || isLoading}
              onClick={() => void handleReceiveAndRelease()}>
              {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : <Truck className="mr-2 h-4 w-4" />}
              {t('receive.and.release.button.label', 'Receive and Release')}
            </ActionButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
